/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, memo, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Handle } from 'reactflow';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';

// Component imports
import { Space, Typography } from 'antd';
import NodeWrapper from '../../NodeWrapper';
import LimitedConnectionsHandle from '../../LimitedConnectionsHandle';
import { CSATStarIcon } from '../../../Icons/CSATStarIcon';
import { CSATNumberIcon } from '../../../Icons/CSATNumberIcon';
import { CSATEmojiesIcon } from '../../../Icons/CSATEmojiesIcon';

// Misc imports
import { CSAT_NODE_RATING_STYLES, NODE_TYPES_PROPERTIES } from '../../../../constants/flowGraph';
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';
import { GRAPH_LAYOUTS } from '../../../../constants/flowGraph';
import { GraphContext } from '../../FlowGraph';
import { truncateMarkdown } from '../../../../utils/string/truncateMarkdown';

// Constant declarations
const { Text } = Typography;

const CsatNode = ({ id, type, data, dragging, selected, isConnectable }) => {
  const nodeProps = NODE_TYPES_PROPERTIES[type];
  const { t } = useTranslation();
  const { graphDirection } = useContext(GraphContext);
  const { currentBotLanguage } = useBotLanguage();

  const renderRating = () => {

    let ratingStyleIcons = null;
    switch (data.ratingStyle) {
      case CSAT_NODE_RATING_STYLES.NUMBERS:
        ratingStyleIcons = <Typography.Text className="pb-1">
          {
            Array.from({ length: 5 }, (_, i) => i + 1).map((index) => (
              <CSATNumberIcon
                className="ml-1 mb-1"
                key={index}
                index={index}
              />
            ))
          }
        </Typography.Text>;
        break;
      case CSAT_NODE_RATING_STYLES.STARS:
        ratingStyleIcons = <Typography.Text className="pb-1">
          {
            Array.from({ length: 5 }, (_, i) => i + 1).map((index) => (
              <CSATStarIcon key={index} />
            ))
          }
        </Typography.Text>;
        break;
      case CSAT_NODE_RATING_STYLES.EMOTIONS:
        ratingStyleIcons = <Typography.Text className="pb-1">
          {
            Array.from({ length: 3 }, (_, i) => i + 1).map((index) => (
              <CSATEmojiesIcon key={index} index={index} className="ml-1" />
            ))
          }
        </Typography.Text>;
        break;
      default:
        ratingStyleIcons = null;
        break;
    }

    return (
      <Space className="w-100 d-flex justify-content-center align-items-center" direction="vertical">
        {
          data?.label?.[currentBotLanguage] ? (
            <Text className="pb-1 text-wrap">
              <ReactMarkdown
                skipHtml
                linkTarget="_blank"
              >
                {
                  truncateMarkdown(data.label[currentBotLanguage], {
                    limit: 45,
                    ellipsis: true,
                    replaceImages: true,
                    replaceBlankSpaces: true
                  })
                }
              </ReactMarkdown>
            </Text>
          ) : (
            <Text type="secondary" className="text-wrap">
              {t('Write some content')}
            </Text>
          )
        }
        {ratingStyleIcons}
      </Space>
    );
  };

  const NodeContents = useMemo(() => {
    return (
      <Fragment>
        <Handle type="target" position={graphDirection === GRAPH_LAYOUTS.VERTICAL ? 'top' : 'left'} isConnectable={isConnectable} />

        <Space size={6} className="node-header">
          {nodeProps.icon}
          {nodeProps.label}
        </Space>

        <Text type="secondary" ellipsis>
          {data?.ratingStyle ? renderRating() : t('Click to add details')}
        </Text>

        <LimitedConnectionsHandle id={id} type="source" position={graphDirection === GRAPH_LAYOUTS.VERTICAL ? 'bottom' : 'right'} allowedOutgoingEdgesCount={1} />
      </Fragment>
    );
  }, [data, nodeProps, graphDirection, currentBotLanguage]);

  return (
    <NodeWrapper
      id={id}
      type={type}
      data={data}
      children={NodeContents}
      dragging={dragging}
      selected={selected}
    />
  );
};

// https://reactflow.dev/docs/api/nodes/node-options/
CsatNode.propTypes = {
  data: PropTypes.object,
  id: PropTypes.string,
  isConnectable: PropTypes.bool,
  dragging: PropTypes.bool,
  selected: PropTypes.bool,
  type: PropTypes.string,
  xPos: PropTypes.number,
  yPos: PropTypes.number
};

export default memo(CsatNode);
